import {
  AGENT_TONE_VALUES,
  VOICE_OPTIONS,
  type AgentTone,
  type TemplateVariable,
} from '@super/shared-types'
import { PhoneIncoming, PhoneOutgoing } from 'lucide-react'

export const DIRECTION_CONFIG = {
  OUTBOUND: { icon: PhoneOutgoing, label: 'Outbound' },
  INBOUND: { icon: PhoneIncoming, label: 'Inbound' },
} as const

export const toneOptions = AGENT_TONE_VALUES

export const llmOptions = [
  { id: 'gpt-4o-mini', label: 'GPT-4o Mini', description: 'OpenAI - fastest' },
  { id: 'gpt-4-turbo', label: 'GPT-4 Turbo', description: 'OpenAI - balanced' },
  { id: 'gpt-4o', label: 'GPT-4o', description: 'OpenAI - most capable' },
]

export const sttOptions = [
  { id: 'nova-3', label: 'Nova', description: 'Deepgram' },
]

export { VOICE_OPTIONS }

/** Variable name keys for prompt interpolation (enum-like). */
export type VariableKey =
  | 'firstName'
  | 'lastName'
  | 'fullName'
  | 'email'
  | 'phone'
  | 'property'
  | 'comments'
  | 'moveInDate'
  | 'pets'
  | 'numberOfBedrooms'
  | 'numberOfBathrooms'
  | 'numberOfOccupants'
  | 'leaseLengthMonths'
  | 'income'
  | 'creditScoreMin'
  | 'creditScoreMax'
  | 'listingURL'
  | 'companyName'
  | 'agentName'
  // Collections (payment.overdue) variables — `property` is reused from Lead
  | 'address'
  | 'unitNumber'
  | 'amountOwed'
  | 'dueDate'
  | 'daysOverdue'
  // Emergency/scenario variables — `property` is reused from Lead
  | 'unit'
  | 'contactName'
  | 'issueDescription'

/**
 * Which trigger/flow a variable belongs to. `SHARED` means it is populated for
 * every trigger (lead, collections, etc.) and must always be selectable.
 */
export type VariableScope = 'SHARED' | 'LEAD' | 'COLLECTIONS' | 'EMERGENCY'

/** Semantic data type of a variable's value — used to filter context-specific pickers. */
export type VariableDataType =
  | 'string'
  | 'phone'
  | 'phone[]'
  | 'number'
  | 'date'
  | 'email'

/** Variable for prompt interpolation. Uses generic TemplateVariable with enum keys. */
export type Variable = TemplateVariable<VariableKey> & {
  description: string
  example: string
  /** If true, variable is always present; optional variables may be missing. */
  required: boolean
  scope: VariableScope
  dataType: VariableDataType
}

/** Base variables available for all agent types (provided server-side regardless of trigger). */
export const BASE_VARIABLES: readonly Variable[] = [
  {
    name: 'agentName',
    description: 'Agent name (auto-filled)',
    example: 'Leasing Bot',
    required: false,
    scope: 'SHARED',
    dataType: 'string',
  },
  {
    name: 'companyName',
    description: 'Your company name (auto-filled)',
    example: 'Acme Properties',
    required: false,
    scope: 'SHARED',
    dataType: 'string',
  },
]

/** Available variables for prompt interpolation (from webhook DTO). Uses camelCase. */
export const AVAILABLE_VARIABLES: readonly Variable[] = [
  ...BASE_VARIABLES,
  {
    name: 'firstName',
    description: 'Contact first name',
    example: 'John',
    required: false,
    scope: 'SHARED',
    dataType: 'string',
  },
  {
    name: 'lastName',
    description: 'Contact last name',
    example: 'Doe',
    required: false,
    scope: 'SHARED',
    dataType: 'string',
  },
  {
    name: 'fullName',
    description: 'Contact full name (First Last)',
    example: 'John Doe',
    required: false,
    scope: 'SHARED',
    dataType: 'string',
  },
  {
    name: 'email',
    description: 'Contact email address',
    example: 'john@example.com',
    required: false,
    scope: 'SHARED',
    dataType: 'email',
  },
  {
    name: 'phone',
    description: 'Contact phone number',
    example: '+15551234567',
    required: true,
    scope: 'SHARED',
    dataType: 'phone',
  },
  {
    name: 'property',
    description: 'Property name or address',
    example: '123 Main St',
    required: true,
    scope: 'SHARED',
    dataType: 'string',
  },
  {
    name: 'listingURL',
    description: 'Listing URL (auto-matched from property)',
    example: 'https://example.com/showing',
    required: false,
    scope: 'LEAD',
    dataType: 'string',
  },
  {
    name: 'comments',
    description: 'Message or notes from lead',
    example: 'Interested in 2BR',
    required: false,
    scope: 'LEAD',
    dataType: 'string',
  },
  {
    name: 'moveInDate',
    description: 'Desired move-in date',
    example: '2026-03-01',
    required: false,
    scope: 'LEAD',
    dataType: 'date',
  },
  {
    name: 'pets',
    description: 'Pet information',
    example: '1 dog',
    required: false,
    scope: 'LEAD',
    dataType: 'string',
  },
  {
    name: 'numberOfBedrooms',
    description: 'Number of bedrooms',
    example: '3',
    required: false,
    scope: 'LEAD',
    dataType: 'number',
  },
  {
    name: 'numberOfBathrooms',
    description: 'Number of bathrooms',
    example: '2',
    required: false,
    scope: 'LEAD',
    dataType: 'number',
  },
  {
    name: 'numberOfOccupants',
    description: 'Number of occupants',
    example: '2',
    required: false,
    scope: 'LEAD',
    dataType: 'number',
  },
  {
    name: 'leaseLengthMonths',
    description: 'Lease length in months',
    example: '12',
    required: false,
    scope: 'LEAD',
    dataType: 'number',
  },
  {
    name: 'income',
    description: 'Annual income',
    example: '100000',
    required: false,
    scope: 'LEAD',
    dataType: 'number',
  },
  {
    name: 'creditScoreMin',
    description: 'Credit score minimum',
    example: '660',
    required: false,
    scope: 'LEAD',
    dataType: 'number',
  },
  {
    name: 'creditScoreMax',
    description: 'Credit score maximum',
    example: '690',
    required: false,
    scope: 'LEAD',
    dataType: 'number',
  },
  {
    name: 'address',
    description: 'Property address',
    example: '25 Great Jones St',
    required: false,
    scope: 'COLLECTIONS',
    dataType: 'string',
  },
  {
    name: 'unitNumber',
    description: 'Unit number',
    example: '2B',
    required: false,
    scope: 'COLLECTIONS',
    dataType: 'string',
  },
  {
    name: 'amountOwed',
    description: 'Amount owed (numeric value as sent by the integrator)',
    example: '1450',
    required: false,
    scope: 'COLLECTIONS',
    dataType: 'number',
  },
  {
    name: 'dueDate',
    description: 'Payment due date (YYYY-MM-DD)',
    example: '2026-04-01',
    required: false,
    scope: 'COLLECTIONS',
    dataType: 'date',
  },
  {
    name: 'daysOverdue',
    description: 'Days past due',
    example: '16',
    required: false,
    scope: 'COLLECTIONS',
    dataType: 'number',
  },
  {
    name: 'unit',
    description: 'Unit number (emergency/scenario flows)',
    example: '2B',
    required: false,
    scope: 'EMERGENCY',
    dataType: 'string',
  },
  {
    name: 'contactName',
    description: 'Contact name for the emergency',
    example: 'Jane Doe',
    required: false,
    scope: 'EMERGENCY',
    dataType: 'string',
  },
  {
    name: 'issueDescription',
    description: 'Short description of the emergency issue',
    example: 'Water leak in unit',
    required: false,
    scope: 'EMERGENCY',
    dataType: 'string',
  },
]

/**
 * Filter `AVAILABLE_VARIABLES` by trigger. `SHARED` variables are always
 * included. Unknown or undefined triggers return the full list so callers
 * that do not yet know their trigger (e.g. a fresh form) are never starved.
 *
 * `SCENARIO` currently maps to `EMERGENCY` because emergency is the only
 * scenario type that dispatches outbound calls today; broaden when
 * MAINTENANCE/LEASING scenarios start firing dial-outs.
 */
export function getVariablesForTriggerType(
  triggerType: string | null | undefined,
): readonly Variable[] {
  if (triggerType === 'LEAD_WEBHOOK') {
    return AVAILABLE_VARIABLES.filter(
      v => v.scope === 'LEAD' || v.scope === 'SHARED',
    )
  }
  if (triggerType === 'PAYMENT_OVERDUE_WEBHOOK') {
    return AVAILABLE_VARIABLES.filter(
      v => v.scope === 'COLLECTIONS' || v.scope === 'SHARED',
    )
  }
  if (triggerType === 'SCENARIO') {
    return AVAILABLE_VARIABLES.filter(
      v => v.scope === 'EMERGENCY' || v.scope === 'SHARED',
    )
  }
  return AVAILABLE_VARIABLES
}

const DIAL_OUT_TRIGGER_TYPES = [
  'LEAD_WEBHOOK',
  'PAYMENT_OVERDUE_WEBHOOK',
  'SCENARIO',
] as const

/** Variables eligible for dial-out: scope-filtered by trigger type, then restricted to
 *  phone-compatible data types (`phone` or `phone[]`).
 *  Returns an empty array when no trigger is selected or the trigger is unrecognized —
 *  dial-out variables are trigger-local, not global. */
export function getDialOutVariables(
  triggerType: string | null | undefined,
): readonly Variable[] {
  if (
    !triggerType ||
    !(DIAL_OUT_TRIGGER_TYPES as readonly string[]).includes(triggerType)
  )
    return []
  return getVariablesForTriggerType(triggerType).filter(
    v => v.dataType === 'phone' || v.dataType === 'phone[]',
  )
}

export type { AgentTone }

export function getTemperatureLabel(value: number): string {
  if (value === 0) return 'Precise'
  if (value === 0.25) return 'Consistent'
  if (value === 0.5) return 'Balanced'
  if (value === 0.75) return 'Creative'
  if (value === 1) return 'Experimental'
  return `${value}`
}
